"use client"

import { observer } from "mobx-react-lite"
import { Bar, BarChart, CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts"
import { GenerativeAvatar } from "@/components/GenerativeAvatar"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { getChartColor, getDefaultTheme, useTheme } from "@/hooks/useTheme"
import { cn } from "@/lib/utils"
import type { ThemeConfigType } from "@/stores/DeveloperInsightsStore"

interface ChartDataPoint {
  label: string
  value: number
}

interface MetricsCardProps {
  title: string
  description: string
  subtitle?: string
  percentage: number
  trend: "up" | "down" | "neutral"
  chartData: ChartDataPoint[]
  chartType: "line" | "bar"
  theme?: ThemeConfigType
  className?: string
  trendLabel?: string
  distributionLabel?: string
  maxValue?: number // Optional: for normalizing Y-axis across multiple charts
  /** Avatar configuration (standard avatar with image/fallback) */
  avatar?: {
    src?: string
    fallback: string
    alt?: string
  }
  /** Generative avatar configuration (for teams/repositories) */
  generativeAvatar?: {
    name: string
    type: "team" | "repository"
    organizationName?: string
  }
}

export const MetricsCard = observer(
  ({
    title,
    description,
    subtitle,
    percentage,
    trend,
    chartData,
    chartType,
    theme,
    className,
    trendLabel = "Trend",
    distributionLabel = "Distribution",
    maxValue: propMaxValue,
    avatar,
    generativeAvatar,
  }: MetricsCardProps) => {
    const activeTheme = theme || getDefaultTheme()
    const themeClasses = useTheme(activeTheme)
    const isDefaultTheme = !theme
    const chartColor = getChartColor(activeTheme, 0)

    // Calculate max value for consistent Y-axis across line charts
    // Use provided maxValue if available, otherwise calculate from data
    const calculatedMaxValue =
      chartType === "line"
        ? Math.max(...chartData.map((d) => d.value), 1) // Ensure at least 1 for empty data
        : undefined

    const maxValue = propMaxValue ?? calculatedMaxValue

    const yAxisDomain: [number, number] | undefined =
      chartType === "line" && maxValue !== undefined ? [0, maxValue] : undefined

    const chartConfig = {
      value: {
        label: "Value",
        color: chartColor,
      },
    } satisfies ChartConfig

    const badgeVariant = trend === "up" ? "default" : trend === "down" ? "destructive" : "secondary"
    const badgeColor =
      trend === "up"
        ? "bg-green-500 hover:bg-green-600"
        : trend === "down"
          ? "bg-orange-500 hover:bg-orange-600"
          : ""

    return (
      <Card
        className={cn(
          "w-full overflow-hidden",
          !isDefaultTheme && themeClasses.card,
          !isDefaultTheme && themeClasses.spacing,
          !isDefaultTheme && themeClasses.borderRadius,
          !isDefaultTheme && themeClasses.colorScheme,
          className,
        )}
      >
        <CardHeader>
          <div className="flex items-start gap-3">
            {generativeAvatar && (
              <GenerativeAvatar
                name={generativeAvatar.name}
                type={generativeAvatar.type}
                organizationName={generativeAvatar.organizationName}
                size="md"
              />
            )}
            {!generativeAvatar && avatar && (
              <Avatar className="h-8 w-8 flex-shrink-0">
                {avatar.src && <AvatarImage src={avatar.src} alt={avatar.alt || title} />}
                <AvatarFallback className="text-xs">{avatar.fallback}</AvatarFallback>
              </Avatar>
            )}
            <div className="flex-1 min-w-0">
              <CardTitle className={cn(!isDefaultTheme && themeClasses.typography)}>
                {title}
              </CardTitle>
              {subtitle && (
                <CardDescription className="text-xs text-muted-foreground">
                  {subtitle}
                </CardDescription>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div
            className={cn(
              "grid grid-cols-2 gap-6",
              theme?.spacing === "compact" && "gap-4",
              theme?.spacing === "comfortable" && "gap-8",
            )}
          >
            {/* Left Column */}
            <div className="space-y-4">
              <div>
                <p className={cn("text-2xl font-bold", !isDefaultTheme && themeClasses.typography)}>
                  {description}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Badge variant={badgeVariant} className={cn("w-fit", badgeColor)}>
                  {percentage > 0 ? "+" : ""}
                  {percentage.toFixed(1)}%
                </Badge>
                <div
                  className={cn(
                    "flex items-center justify-center text-sm text-muted-foreground",
                    !isDefaultTheme && themeClasses.typography,
                  )}
                >
                  {chartType === "line" ? trendLabel : distributionLabel}
                </div>
              </div>
            </div>

            {/* Right Column - Chart */}
            <div>
              <ChartContainer config={chartConfig} className="h-[120px] sm:h-[150px] w-full">
                {chartType === "line" ? (
                  <LineChart data={chartData} accessibilityLayer>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      tickMargin={8}
                      axisLine={false}
                      tick={{ fontSize: "10px" }}
                      angle={-45}
                      textAnchor="end"
                      height={40}
                    />
                    <YAxis hide domain={yAxisDomain} />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="var(--color-value)"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                ) : (
                  <BarChart data={chartData} accessibilityLayer>
                    <CartesianGrid vertical={false} />
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      tickMargin={8}
                      axisLine={false}
                      tick={{ fontSize: "10px" }}
                      angle={-45}
                      textAnchor="end"
                      height={40}
                    />
                    <YAxis hide />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Bar dataKey="value" fill="var(--color-value)" radius={4} />
                  </BarChart>
                )}
              </ChartContainer>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  },
)

MetricsCard.displayName = "MetricsCard"
